import React, { useState } from "react";
import forwardArrow from "../../../assets/icons/forward.svg";
import Card from "./card";
import accountService from "../../../api/services/accountService";
import * as moment from "moment";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Schedule from "../../../assets/icons/Schedule.svg";
import { useDispatch, useSelector } from "react-redux";
import { playSong } from "../../common/actions/common.actions";
import { selectLoginData } from "../login/login.actions";
import { FiSearch } from "react-icons/fi";
import { BiSolidCalendar } from "react-icons/bi";
import whiteSchedule from '../../../assets/icons/whiteSchedule.svg';
import { IoIosArrowBack } from "react-icons/io";
const Account = ({ closeSidebar, setIsLoading }) => {
    const [transactionData, setTransactionData] = useState([]);
    const today = new Date();
    const [dateRange, setDateRange] = useState([today, today]);
    const [startDate, endDate] = dateRange;
    const [transactionsAvailable, setTransactionsAvailable] = useState(true);
    const { userDetails } = useSelector(selectLoginData);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const handleSongClick = async () => {
        dispatch(playSong());
    };

    const handleClick = () => {
        handleSongClick();
        navigate("/dashboard");
    };

    const handleDateChange = (update) => {
        setDateRange(update);
    };

    const handleSearchClick = () => {
        fetchData(startDate, endDate);
    }

    const fetchData = async (start, end) => {
        if (start && end) {
            setIsLoading(true);
            const formattedStartDate = moment(start).format("YYYY-MM-DD");
            const formattedEndDate = moment(end).format("YYYY-MM-DD");
            try {
                const response = await accountService.getTransactionsForUser({
                    userId: Number(userDetails.userId),
                    startDate: formattedStartDate,
                    endDate: formattedEndDate,
                });
                const data = await response.json();

                if (data?.userTransactions) {
                    const filteredData = data?.userTransactions.filter(
                        (transaction) =>
                            moment(transaction?.date).isSameOrAfter(start, "day") &&
                            moment(transaction?.date).isSameOrBefore(end, "day")
                    );
                    setTransactionData(filteredData);
                    setTransactionsAvailable(filteredData.length > 0);
                } else {
                    setTransactionsAvailable(false);
                }
            } catch (error) {
                console.error("API request error:", error);
            } finally {
                setIsLoading(false);
            }
        }
    };

    return (
        <div className="w-full h-screen flex flex-col font-dangrek bg-gradient-to-br from-[#EAEAEA] via-green-50 to-[#228B22]/10 relative overflow-hidden">
            {/* Football Background Animation */}
            <div className="absolute inset-0 pointer-events-none opacity-20">
                {[...Array(6)].map((_, i) => (
                    <div
                        key={`account-line-${i}`}
                        className="absolute w-full h-px bg-[#228B22] opacity-30"
                        style={{ top: `${(i + 1) * 16.66}%` }}
                    />
                ))}

                {/* Floating Football and Money Icons */}
                {[...Array(8)].map((_, i) => (
                    <div
                        key={`account-icon-${i}`}
                        className="absolute text-lg opacity-10"
                        style={{
                            left: `${10 + i * 12}%`,
                            top: `${20 + (i % 3) * 25}%`,
                            color: i % 3 === 0 ? '#228B22' : i % 3 === 1 ? '#FFD700' : '#1E90FF',
                            animation: `floatingAccountIcon 8s ease-in-out infinite ${i * 1.5}s`
                        }}
                    >
                        {i % 3 === 0 ? '⚽' : i % 3 === 1 ? '💰' : '$'}
                    </div>
                ))}
            </div>

            {/* CSS Animation */}
            <style jsx>{`
                @keyframes floatingAccountIcon {
                    0%, 100% { transform: translateY(0px) scale(1); }
                    50% { transform: translateY(-15px) scale(1.1); }
                }
            `}</style>

            {/* Header */}
            <div className="relative z-10 bg-gradient-to-r from-[#228B22] via-emerald-600 to-[#1F5F1F] py-6 px-4 shadow-lg backdrop-blur-sm">
                <div className="flex items-center">
                    <button
                        className="w-10 h-10 mr-4 flex justify-center items-center text-white hover:bg-white/20 rounded-xl transition-all duration-200"
                        onClick={handleClick}
                    >
                        <IoIosArrowBack size={25} />
                    </button>
                    <div>
                        <h3 className="text-white font-bold text-xl">{t("statement")}</h3>
                        <p className="text-emerald-100 text-xs font-medium">Account Transactions</p>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="relative z-10 flex-1 overflow-y-auto bg-white/50 backdrop-blur-sm">
                {/* Date Filter Section */}
                <div className="p-4">
                    <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 border border-[#228B22]/20 shadow-lg">
                        <div className="flex justify-between items-center">
                            <label
                                htmlFor="accountDate"
                                className="flex items-center bg-white/70 rounded-xl py-3 px-4 border border-[#228B22]/10 shadow-sm cursor-pointer hover:shadow-md transition-all"
                            >
                                <div className="w-8 h-8 bg-[#228B22] rounded-full flex items-center justify-center mr-3">
                                    <BiSolidCalendar size={18} className="text-white" />
                                </div>
                                <div>
                                    <p className="text-[#228B22] font-bold text-sm">Date Range</p>
                                    <p className="text-gray-600 text-xs font-medium">
                                        {moment(startDate).format("DD-MM-YYYY")} - {moment(endDate).format("DD-MM-YYYY")}
                                    </p>
                                </div>
                            </label>
                            <DatePicker
                                id="accountDate"
                                selectsRange={true}
                                startDate={startDate}
                                endDate={endDate}
                                onChange={handleDateChange}
                                dateFormat="yyyy-MM-dd"
                                withPortal
                                className="rounded bg-white hidden"
                            />
                            <button
                                className="w-12 h-12 bg-gradient-to-r from-[#FFD700] to-yellow-400 rounded-xl flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
                                onClick={handleSearchClick}
                            >
                                <FiSearch size={20} className="text-gray-900" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Transactions List */}
                <div className="px-4 pb-4">
                    {transactionsAvailable ? (
                        <div className="space-y-4">
                            {transactionData?.map((data, index) => (
                                <Card data={data} key={index} />
                            ))}
                        </div>
                    ) : (
                        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-[#228B22]/20 shadow-lg text-center">
                            <div className="w-16 h-16 bg-[#228B22]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                <span className="text-2xl">📊</span>
                            </div>
                            <p className="text-gray-600 font-medium">{t("noTransaction")}</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Account;
